import { getNextTypography, makeStyles } from '@paintscout/ui'
import type { MuiTheme as Theme } from '@paintscout/ui'

/**
 * Hover treatment for the Issued/Expires date chip: a subtle rounded P97 pill, with negative margins so
 * it doesn't shift layout. The column links use {@link linkHoverTextStyles} instead.
 */
function linkHoverPillStyles(theme: Theme) {
  return {
    cursor: 'pointer',
    borderRadius: theme.borderRadius.sm,
    margin: theme.spacing(-0.25, -0.5),
    padding: theme.spacing(0.25, 0.5),
    transition: 'background-color 150ms ease',
    '&:hover': {
      backgroundColor: theme.paintscout.palette.brand.Neutral.P97
    }
  }
}

/**
 * Hover for the detail links inside a contact column (name, email, phone, address): no pill, the text
 * and icon go blue instead. The column's own grey wash drops back to white while one of these is
 * hovered (see `clickable`), so the link reads as the target rather than the whole card (ENG-2032,
 * matches the prototype).
 */
function linkHoverTextStyles(theme: Theme) {
  return {
    cursor: 'pointer',
    transition: 'color 150ms ease',
    '&:hover, &:hover *': {
      color: theme.paintscout.palette.brand.Blue.Blue70
    }
  }
}

/**
 * The document header's styles, shared by the desktop layout, the mobile layout, and the small pieces
 * they have in common (HeaderLineLink, the contact menu's divider). One sheet rather than one per file
 * because the two layouts share the card shell, the accent tick, the link-hover pill, and the
 * `clickableEmpty → $emptyState` rule — a JSS rule reference only resolves within its own sheet.
 */
export const useQuoteDocumentHeaderStyles = makeStyles(
  (theme: Theme) => {
    const neutral = theme.paintscout.palette.brand.Neutral
    const type = getNextTypography(theme)
    // P90 for the rules inside the card; the card's own outline is P85 (ENG-2094).
    const border = `1px solid ${neutral.P90}`
    const linkHoverPill = linkHoverPillStyles(theme)
    const linkHoverText = linkHoverTextStyles(theme)
    return {
      root: {
        border: `1px solid ${neutral.P85}`,
        borderRadius: 8,
        backgroundColor: neutral.White,
        overflow: 'hidden',
        marginBottom: theme.spacing(2)
      },

      // When embedded at the top of a shared card (with the item section), the header drops its own
      // border/rounding/gap so it reads as the top region of that one card rather than a separate one.
      flush: {
        border: 'none',
        borderRadius: 0,
        marginBottom: 0
      },

      // --- company section ---
      companySection: {
        display: 'flex',
        alignItems: 'flex-start',
        justifyContent: 'space-between',
        // Never wrap: the doc label + ID stay pinned to the right at any width, and long company info
        // wraps within its own column instead (see companyBlock's zero flex-basis).
        flexWrap: 'nowrap',
        gap: theme.spacing(3),
        padding: theme.spacing(4),
        [theme.breakpoints.down('xs')]: {
          padding: theme.spacing(2.5)
        }
      },

      // Whole-card edit affordance: pointer + subtle hover wash so it reads as clickable, matching the
      // EditContact/EditDeal detail cards. Applied to each editable region (columns + company block).
      clickable: {
        cursor: 'pointer',
        transition: 'background-color 150ms ease',
        '&:hover': {
          backgroundColor: neutral.P98
        },
        '&:has($lineLink:hover), &:has($contactNameLink:hover)': {
          backgroundColor: neutral.White
        }
      },
      // An *empty* clickable column hovers differently: instead of washing the whole column grey, the
      // dashed placeholder inside it lights up blue so it reads as the button it is (the column has no
      // other content to speak of, so the wash just looked like a dead grey block).
      clickableEmpty: {
        cursor: 'pointer',
        '&:hover $emptyState, &:focus-visible $emptyState': {
          borderColor: theme.palette.primary.main,
          color: theme.palette.primary.main
        }
      },
      // An individual value (phone / email / address) that deep-links (tel:/mailto:/maps) on click.
      // Stops the click from also opening the card's edit dialog. Link blue at rest (ENG-2094); hover
      // underlines it and turns the icon blue too.
      lineLink: {
        ...linkHoverText,
        color: theme.paintscout.palette.brand.Blue.Blue70,
        '&:hover': {
          textDecoration: 'underline'
        },
        // Sized to its text, not the column, so the blue hover starts only on the link itself — a
        // full-width row went blue from the empty space beside a short value (ENG-2086). The max
        // width keeps a long value wrapping within the column.
        width: 'fit-content',
        maxWidth: '100%'
      },
      companyBlock: {
        display: 'flex',
        alignItems: 'flex-start',
        gap: theme.spacing(3),
        // Fill the row so the meta line wraps within this column instead of running into the doc
        // block (Estimate / ID) on the right; minWidth:0 lets it actually shrink and wrap.
        // A *zero* basis is what keeps the doc block on the right: with `auto`, this column's flex
        // base is the meta row's max-content width, which overflows the row and pushes the doc block
        // to the next line before the meta row ever wraps.
        flex: '1 1 0',
        minWidth: 0
      },
      logo: {
        maxWidth: 120,
        maxHeight: 120,
        objectFit: 'contain',
        flexShrink: 0
      },
      companyInfo: {
        display: 'flex',
        flexDirection: 'column',
        minWidth: 0,
        paddingTop: theme.spacing(0.5)
      },
      heading: {
        ...type.documentTitle,
        lineHeight: 1.2
      },
      metaRow: {
        display: 'flex',
        flexWrap: 'wrap',
        alignItems: 'center',
        gap: theme.spacing(2.5),
        marginTop: theme.spacing(1.25),
        ...type.body
      },
      // Free-text "Additional Company Info" from settings (companyAdditional / the quote-type company
      // override). Sits under the meta row with the same accent tick the column labels use, so it reads
      // as a deliberate note rather than another meta item. Newlines in the setting are preserved.
      additionalRow: {
        display: 'flex',
        alignItems: 'flex-start',
        gap: theme.spacing(0.75),
        marginTop: theme.spacing(1.25)
      },
      // Same 3x12 accent tick the column labels use. It keeps its fixed height (rather than stretching
      // with the text) so a multi-line note still reads as one chip beside its first line — hence the
      // small top offset that centres it against that line.
      additionalTick: {
        marginTop: 4,
        marginLeft: '7.5px',
        marginRight: '9px'
      },
      additionalText: {
        ...type.body,
        whiteSpace: 'pre-line',
        minWidth: 0
      },
      metaItem: {
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(0.75),
        minWidth: 0
      },
      // Font Awesome fills its em box, where the Material icon this replaced drew its glyph inset in a
      // 24-unit viewBox (~80% ink). Sizes are scaled by 0.8 so the icons read at their old size (ENG-1366).
      metaIcon: {
        fontSize: 14,
        color: neutral.P50
      },
      // Issued/Expires renders as a plain inline meta item (icon + text) matching the address/phone
      // items — no chip/pill background. It's still the one clickable affordance in the header (opens
      // the same date/expiry editor the classic estimate header used, QuoteDateCard).
      datePill: {
        display: 'inline-flex',
        alignItems: 'center',
        gap: theme.spacing(0.75),
        ...type.meta,
        whiteSpace: 'nowrap'
      },
      datePillClickable: {
        ...linkHoverPill
      },
      // Padded panel that hosts the Issued/Expiry editor (two collapsible calendar cards).
      datePopover: {
        padding: theme.spacing(0.75),
        minWidth: 320
      },
      docBlock: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-end',
        flexShrink: 0,
        // Keeps "Work Order" / "Estimate" and the ID on single lines at the right edge.
        whiteSpace: 'nowrap',
        marginLeft: 'auto',
        paddingTop: theme.spacing(0.5)
      },
      docId: {
        marginTop: theme.spacing(1.25),
        ...type.meta
      },

      // --- columns section ---
      // Flex-wrap (not a fixed grid) reflows the three contact columns from 3-up → 2-up → stacked
      // based on the card's actual width (so it adapts to mobile *and* to the sidebar squeezing the
      // estimate column), rather than a fixed viewport breakpoint. `flex-grow` makes each column fill
      // its row, so a lone column that wraps onto its own row stretches full-width instead of leaving
      // a grey half-cell.
      //
      // Dividers are painted box-shadows on each column (see `column`), clipped here. They used to be
      // a 1px flex gap over a P90 background, but a gap is the *absence* of paint: `flex-grow` gives
      // the columns fractional widths, and when the browser snaps their backgrounds to device pixels
      // the two rects can round toward each other and close the gap, so the divider silently vanishes
      // at some container widths / display scalings. A painted shadow always gets its pixel.
      columns: {
        display: 'flex',
        flexWrap: 'wrap',
        borderTop: border,
        // Clips the leading divider of every row (see `column`) — this is what keeps the shadow
        // approach correct at any column count and wrap point.
        overflow: 'hidden'
      },
      // Compact columns (mock #23): tighter padding, gaps, and type so the row reads dense.
      column: {
        position: 'relative',
        flex: '1 1 200px',
        display: 'flex',
        flexDirection: 'column',
        gap: theme.spacing(1),
        padding: theme.spacing(2, 3),
        minWidth: 0,
        backgroundColor: neutral.White,
        // Left + top dividers, drawn 1px outside the box so each column separates itself from the one
        // before it (and, once wrapped, from the row above). Outer shadows paint outside the border
        // box, so the first column of *every* row has its left shadow land outside `columns` and get
        // clipped — which a `:not(:first-child)` border can't do once the columns wrap onto a second
        // row. Columns are `position: relative`, so each paints over its predecessor in DOM order.
        boxShadow: `-1px 0 0 ${neutral.P90}, 0 -1px 0 ${neutral.P90}`,
        [theme.breakpoints.down('xs')]: {
          padding: theme.spacing(1.5)
        }
      },
      labelRow: {
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(0.75)
      },
      tick: {
        width: 3,
        height: 12,
        borderRadius: 2,
        backgroundColor: theme.paintscout.palette.brand.Neutral.P50,
        flexShrink: 0
      },
      // An empty column's tick drops to a muted neutral, so the accent reads as "this has content" and
      // the empty column sits back visually next to its dashed placeholder.
      tickEmpty: {
        backgroundColor: neutral.P85
      },
      columnLabel: {
        ...type.fieldLabel
      },
      nameGroup: {
        display: 'flex',
        flexDirection: 'column',
        gap: 1,
        minWidth: 0
      },
      primaryText: {
        ...type.rowTitle
      },
      // Clickable contact name → opens the contact profile. Text colour at rest, blue on hover; stops
      // propagation so it doesn't also open the column's edit menu.
      contactNameLink: {
        display: 'inline-block',
        ...linkHoverText
      },
      mutedText: {
        ...type.meta
      },
      // The icon pins to the value's first line, because a long value wraps onto more lines.
      lineItem: {
        display: 'flex',
        alignItems: 'flex-start',
        gap: theme.spacing(0.75),
        ...type.body,
        // Lets the value shrink: a flex item's min-width is otherwise its min-content width, so a
        // long email widened the column instead of wrapping (ENG-1635).
        minWidth: 0,
        overflowWrap: 'anywhere'
      },
      /**
       * Wraps a value inside its column, which is only 200px at its flex basis (ENG-1635). An
       * unbreakable token, such as a long email, breaks at any character instead of overflowing.
       *
       * These values used to ellipsize, but that hid most of a long name, company or email, most
       * often beside a Secondary Contact column (ENG-2608, and ENG-2348 for the PDF).
       */
      wrapValue: {
        minWidth: 0,
        overflowWrap: 'anywhere'
      },
      // Holds a line's icon at the height of one text line and centres it there, so the icon lines up
      // with a one-line value as before and stays beside the first line of a wrapped one. The
      // zero-width space gives the slot the text's own line height.
      lineIconSlot: {
        display: 'flex',
        alignItems: 'center',
        flexShrink: 0,
        '&::before': {
          content: '"\\200b"',
          width: 0
        }
      },
      lineIcon: {
        fontSize: 15,
        color: neutral.P50,
        flexShrink: 0,
        marginTop: 1,
        transition: 'color 150ms ease'
      },
      // A repeated email/phone keeps its icon's slot blank and sits tight under the first (ENG-2035).
      lineIconSpacer: { width: 15, flexShrink: 0 },
      lineItemRepeat: { marginTop: -theme.spacing(0.75) },
      // Reserves the avatar's footprint so a long estimator name wraps before reaching it rather
      // than running underneath (ENG-1635). The avatar is absolute and takes no width, so the inset
      // goes on the two elements it overlaps — padding the column would also indent the detail
      // lines, which it never reaches.
      columnWithAvatar: {
        '& $labelRow, & $nameGroup': {
          paddingRight: 32 + theme.spacing(1)
        }
      },
      // Estimator avatar pinned top-right of the Sales Rep column (mock #23).
      salesRepAvatar: {
        position: 'absolute',
        top: theme.spacing(2.5),
        right: theme.spacing(3),
        [theme.breakpoints.down('xs')]: {
          right: theme.spacing(1.5)
        }
      },
      addressLines: {
        display: 'flex',
        flexDirection: 'column'
      },
      emptyState: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: theme.spacing(0.75),
        minHeight: 52,
        padding: theme.spacing(1.5),
        border: `1px dashed ${neutral.P85}`,
        borderRadius: theme.borderRadius.lg,
        color: neutral.P70,
        fontSize: theme.typography.pxToRem(14),
        fontWeight: 400,
        transition: 'border-color 150ms ease, color 150ms ease',
        '@media print': {
          display: 'none'
        }
      },
      emptyStateIcon: {
        fontSize: 18
      },

      // --- mobile header ---
      // On phones the company block is dropped from the card: the doc label / ID / date sit on one
      // summary row above the compact contact rows, and the company detail moves into the document
      // sheet the summary row opens.
      summaryRow: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: theme.spacing(1.5),
        // 44px tall and 16px in at the sides, as the prototype's summary row (ENG-2080).
        height: 44,
        padding: theme.spacing(0, 2),
        backgroundColor: '#fcfcfd'
      },
      summaryLeft: {
        display: 'flex',
        alignItems: 'baseline',
        gap: theme.spacing(1.5),
        minWidth: 0
      },
      summaryRight: {
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(1.5),
        flexShrink: 0
      },
      summaryLabel: {
        fontSize: theme.typography.pxToRem(13),
        fontWeight: 500,
        color: neutral.P35
      },
      summaryMuted: {
        ...type.meta,
        whiteSpace: 'nowrap'
      },
      summaryRowClickable: {
        cursor: 'pointer',
        '&:hover': {
          backgroundColor: neutral.P98
        }
      },
      // One compact contact row: icon, muted label, then the value. Each row opens the same editor its
      // full-header column does.
      compactRow: {
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(1),
        padding: theme.spacing(1.5, 2),
        borderTop: border,
        minWidth: 0
      },
      compactRowClickable: {
        cursor: 'pointer',
        '&:hover': {
          backgroundColor: neutral.P98
        }
      },
      compactLabel: {
        ...type.fieldLabel,
        flexShrink: 0
      },
      compactValue: {
        ...type.body,
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
        minWidth: 0
      },
      // An unset value reads as the prompt to add one (the row is the button), so it goes blue.
      compactEmptyValue: {
        color: theme.palette.primary.main
      },
      compactIcon: {
        fontSize: 18,
        color: neutral.P50,
        flexShrink: 0
      },

      // --- mobile section sheet (QuoteDocumentHeaderMobileSheet) ---
      // Tapping a compact row opens a bottom sheet with that section's full detail, rather than
      // jumping straight into its editor: on a phone the row can only show one truncated value, so
      // the sheet is where the email/phone/address actually become readable and tappable.
      sheetPaper: {
        borderTopLeftRadius: theme.borderRadius.lg,
        borderTopRightRadius: theme.borderRadius.lg,
        // Never taller than the viewport, and clear of the iOS home indicator.
        maxHeight: '80vh',
        '@supports (max-height: 80dvh)': {
          maxHeight: '80dvh'
        },
        paddingBottom: 'env(safe-area-inset-bottom, 0px)'
      },
      sheetHeader: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: theme.spacing(1.5),
        padding: theme.spacing(1, 1, 1, 2),
        borderBottom: border,
        flexShrink: 0
      },
      sheetTitle: {
        display: 'flex',
        alignItems: 'baseline',
        gap: theme.spacing(1.25),
        ...type.sectionTitle,
        minWidth: 0,
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap'
      },
      // The document ID beside the label reads as the quieter half of the title.
      sheetTitleId: {
        fontWeight: 400
      },
      sheetCloseButton: {
        padding: theme.spacing(0.5),
        color: neutral.P50
      },
      // One padded tray holding the detail rows at the top and the toolbar at the bottom — no divider
      // between them. The minimum height keeps every section's sheet the same size, so opening one row
      // after another doesn't resize the sheet under the reader's thumb.
      sheetTray: {
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'space-between',
        gap: theme.spacing(2),
        minHeight: 275,
        padding: theme.spacing(2.5, 2.5, 4.5),
        overflowY: 'auto'
      },
      sheetRows: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-start',
        gap: theme.spacing(1.5),
        width: '100%',
        minWidth: 0
      },
      // The icon pins to the first line, because a long value wraps rather than truncating.
      sheetRow: {
        display: 'flex',
        alignItems: 'flex-start',
        gap: theme.spacing(1.5),
        maxWidth: '100%',
        minWidth: 0
      },
      // A repeated row's blank icon slot (ENG-2035) — the same width as sheetRowIcon.
      sheetRowIconSpacer: { width: 16, flexShrink: 0 },
      sheetRowIcon: {
        fontSize: 16,
        color: neutral.P50,
        flexShrink: 0,
        // Centres the icon against the value's first line, now that the row aligns to the top.
        marginTop: 8
      },
      // Each value sits in its own outlined pill. A value too long for the sheet wraps inside the pill —
      // the pill grows taller and the corners stay round, rather than the text running off the edge.
      sheetPill: {
        display: 'flex',
        alignItems: 'center',
        minHeight: 32,
        padding: theme.spacing(0.5, 1.5),
        boxSizing: 'border-box',
        minWidth: 0,
        backgroundColor: neutral.White,
        border: `1px solid ${neutral.P85}`,
        borderRadius: 16,
        ...type.body,
        overflowWrap: 'anywhere'
      },
      sheetPillClickable: {
        cursor: 'pointer',
        transition: 'background-color 120ms ease',
        '&:hover': {
          backgroundColor: neutral.P97
        },
        '&:active': {
          backgroundColor: neutral.P97
        }
      },
      sheetToolbar: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'flex-end',
        flexShrink: 0
      },
      // The one action affordance: a 32px round outlined button that opens this section's Edit menu.
      sheetEditButton: {
        width: 32,
        height: 32,
        padding: 0,
        border: `1px solid ${neutral.P85}`,
        backgroundColor: neutral.P98,
        color: neutral.P50,
        '&:hover': {
          backgroundColor: neutral.P97
        }
      },
      // --- document sheet ---
      // The document sheet is read-only company detail, so it drops the pills the editable sections use
      // and renders plain icon + text lines, the way the mock draws it.
      sheetPlainRow: {
        display: 'flex',
        alignItems: 'flex-start',
        gap: theme.spacing(0.75),
        minWidth: 0,
        maxWidth: '100%'
      },
      // Font Awesome fills its em box, where the Material icon this replaced drew its glyph inset in a
      // 24-unit viewBox (~80% ink). Sizes are scaled by 0.8 so the icons read at their old size (ENG-1366).
      sheetPlainIcon: {
        fontSize: 13,
        color: neutral.P50,
        flexShrink: 0,
        // Centres the icon against the value's first line, now that the row aligns to the top.
        marginTop: 2
      },
      sheetPlainText: {
        ...type.body,
        minWidth: 0,
        overflowWrap: 'anywhere'
      },
      sheetCompanyRow: {
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(1.5),
        minWidth: 0
      },
      sheetLogo: {
        width: 72,
        height: 72,
        objectFit: 'contain',
        flexShrink: 0
      },
      sheetCompanyName: {
        ...type.documentTitle,
        minWidth: 0,
        overflowWrap: 'anywhere'
      },
      // The date shares the sheet's bottom row with the toolbar, rather than sitting with the company
      // lines above — it belongs to the document, not to the company.
      sheetDocFooter: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: theme.spacing(1.5),
        flexShrink: 0
      },

      // A thin rule between menu groups (matches the classic ContactCard menu): a disabled, zero-height
      // MenuItem with a bottom border.
      menuItemDivider: {
        padding: 0,
        height: 0,
        minHeight: 0,
        paddingBottom: theme.spacing(),
        marginBottom: theme.spacing(),
        borderBottom: border
      }
    }
  },
  { name: 'QuoteDocumentHeader' }
)

export default useQuoteDocumentHeaderStyles
